import {
    Compiler,
    ComponentRef,
    Inject,
    Injectable,
    InjectFlags,
    InjectionToken,
    Injector,
    NgModuleFactory,
    Type,
    ViewContainerRef,
} from '@angular/core';

import { LoadChildrenType } from './lazy-loader.models';
import { LazyLoaderDirective } from './lazy-loader.module';
import { LAZY_WIDGETS } from './tokens';


/** Injection token that can be used to access the data that was passed in to a dialog. */
export const LAZY_COMPONENT_METADATA = new InjectionToken<any>('DynamicComponentData');

export class LazyLoaderInjector implements Injector {
    constructor(
        private _parentInjector: Injector,
        private _additionalTokens: WeakMap<any, any>
    ) { }

    get<T>(
        token: Type<T> | InjectionToken<T>,
        notFoundValue?: T,
        flags?: InjectFlags
    ): T;
    get(token: any, notFoundValue?: any);
    get(token: any, notFoundValue?: any, flags?: any) {
        const value = this._additionalTokens.get(token);

        if (value) {
            return value;
        }

        return this._parentInjector.get<any>(token, notFoundValue);
    }
}

@Injectable({
    providedIn: 'root'
})
export class LazyLoaderService {

    constructor(
        private injector: Injector,
        private compiler: Compiler,
        @Inject(LAZY_WIDGETS) private lazyWidgets: { [key: string]: () => Promise<NgModuleFactory<any> | Type<any>> }
    ) { }

    // async loadDirective(directive: LazyLoaderDirective) {
    //     return await this.load(directive.appLazy, directive.viewContainerRef, directive.metadata, directive.default);
    // }

    async loadDirective(directive: LazyLoaderDirective) {
        return await this.load(directive, directive.metadata, directive.default);
    }

    async loadOld(name: string, container: ViewContainerRef, data?: any, dataToken: any = LAZY_COMPONENT_METADATA, defaultName: string = null): Promise<ComponentRef<any>> {

        if (container.length === 0) {
            const tempModule = await this.downloadResource(name, defaultName);
            if (!tempModule) {
                return;
            }

            let moduleFactory;

            if (tempModule instanceof NgModuleFactory) {
                // For AOT
                moduleFactory = tempModule;
            } else {
                // For JIT
                moduleFactory = await this.compiler.compileModuleAsync(tempModule);
            }

            let moduleRef: any;
            const entryComponent = (moduleFactory.moduleType as any).entry;

            const map = new WeakMap<any, any>();
            map.set(dataToken, data);
            moduleRef = moduleFactory.create(new LazyLoaderInjector(this.injector, map));

            const compFactory = moduleRef.componentFactoryResolver.resolveComponentFactory(entryComponent);

            container.clear();
            return container.createComponent(compFactory);
        }

        return null;

    }

    async load(directive: LazyLoaderDirective, data?: any, dataToken: any = LAZY_COMPONENT_METADATA, defaultName: string = null): Promise<ComponentRef<any>> {
        const name = directive.appLazy;
        const container = directive.viewContainerRef;

        if (container.length === 0) {

            const { entryComponent, moduleFactory } = await this.getEntryComponent(directive, defaultName);
            if (!entryComponent) {
                return;
            }
           

            const map = new WeakMap<any, any>();
            map.set(dataToken, data);
            const moduleRef = moduleFactory.create(new LazyLoaderInjector(this.injector, map));

            const compFactory = moduleRef.componentFactoryResolver.resolveComponentFactory(entryComponent);

            container.clear();
            return container.createComponent(compFactory);
        }

        return null;

    }


    public async getEntryComponent<T extends Object>(directive: LazyLoaderDirective | string, defaultName: string): Promise<{ entryComponent: T, moduleFactory: any }> {
        const tempModule = await this.downloadResource(directive, defaultName);
        if (!tempModule) {
            return;
        }

        let moduleFactory;

        if (tempModule instanceof NgModuleFactory) {
            // For AOT
            moduleFactory = tempModule;
        } else {
            // For JIT
            moduleFactory = await this.compiler.compileModuleAsync(tempModule);
        }

        const entryComponent = (moduleFactory.moduleType as any).entry;
        return { entryComponent: entryComponent as T, moduleFactory };
    }

    // private async downloadResource(name: string, defaultName: string) {
    private async downloadResource(directive: LazyLoaderDirective | string, defaultName: string) {
        const name = typeof (directive) === 'string' ? directive as string : directive.appLazy;

        let result: NgModuleFactory<any> | Type<any>;
        let loadTarget: LoadChildrenType;
        try {
            if (typeof (directive) !== 'string') directive.loading = true;
            const internalName = Object.getOwnPropertyNames(this.lazyWidgets).find(objProperty => objProperty.toUpperCase() === name.toUpperCase());

            if (!internalName) {
                throw new Error(`${name} is not defined as loading path. Please review in lazyWidgets configuration`);
            }

            loadTarget = this.lazyWidgets[internalName];

            if (loadTarget != null) {
                result = await loadTarget();
            } else {
                throw new Error(`${internalName} is not defined as loading path. Please review in lazyWidgets configuration`);
            }
        } catch (ex) {
            console.error(ex);
            if (defaultName) {
                const internalDefaultName = Object.getOwnPropertyNames(this.lazyWidgets).find(objProperty => objProperty.toUpperCase() === defaultName.toUpperCase());
                loadTarget = this.lazyWidgets[internalDefaultName];

                result = await loadTarget();
            } else {
                throw ex;
            }
        } finally {
            if (typeof (directive) !== 'string') {
                setTimeout(() => {
                    directive.loading = false;
                }, 0);
            }

        }

        return result;
    }

    async processDirectives(views: LazyLoaderDirective[], targetUI?: string) {
        if (views) {
            views.forEach(async viewDirective => {
                const options = viewDirective.metadata && viewDirective.metadata.options;
                if (
                    !options ||
                    ((!options.targetUI && !targetUI) || (options.targetUI) === targetUI)
                ) {
                    // const component = await this.load(viewDirective.appLazy, viewDirective.viewContainerRef, viewDirective.metadata, LAZY_COMPONENT_METADATA, viewDirective.default);
                    const component = await this.load(viewDirective, viewDirective.metadata, LAZY_COMPONENT_METADATA, viewDirective.default);
                    if (component) {
                        viewDirective.componentRef = component;
                    }
                }
            });
        }
    }




}
